import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, ChevronDown, FileText, HandCoins, MoreHorizontal, Plus, Receipt, Truck, Undo2, Wallet } from 'lucide-react';

import { api, durableBusinessPost } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { messageErreur } from '../../lib/erreurs';
import { formatMontant } from '../../lib/formats';
import { libelle, nomLisible, referenceLisible } from '../../lib/libelles';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { compteParDefaut, retenirCompte } from '../../lib/argentApi';
import { financialKey } from '../FinancialStatement';
import ExpenseForm from './ExpenseForm';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';

/**
 * « Nouvelle opération »: un seul bouton dans l'en-tête de l'écran Argent, sur
 * tous les onglets, avec cinq gestes en clair (revue de l'écran Argent,
 * lot 3). Chaque geste ouvre une fenêtre courte avec ses seuls champs; l'envoi
 * suit les règles de toujours: même écriture `/finance/operations`, clé
 * d'idempotence (une nouvelle clé dès qu'un champ change, la même au nouvel
 * essai), réseau nécessaire sauf pour les frais, succès affiché seulement
 * après la réponse du serveur.
 */
export const GESTES = [
  { cle: 'encaisser', menu: 'Encaisser un client', titre: 'Encaisser un client', icone: Wallet },
  { cle: 'depense', menu: 'Enregistrer une dépense', titre: 'Enregistrer une dépense', icone: Receipt },
  { cle: 'avance', menu: 'Avance à un chauffeur', titre: 'Donner une avance à un chauffeur', icone: HandCoins },
  { cle: 'fournisseur', menu: 'Payer un fournisseur', titre: 'Payer un fournisseur', icone: Truck },
  { cle: 'transfert', menu: 'Caisse ↔ banque', titre: 'Déplacer de l’argent entre comptes', icone: ArrowLeftRight },
  // Hors du menu: ouvert depuis le panneau d'un chauffeur.
  { cle: 'rendu', menu: null, titre: 'Reliquat rendu par le chauffeur', icone: Undo2 },
  // Hors du menu: ouvert depuis le panneau d'un fournisseur.
  { cle: 'facture-recue', menu: null, titre: 'Facture reçue d’un fournisseur', icone: FileText },
  { cle: 'autre', menu: 'Autre opération…', titre: 'Autre opération', icone: MoreHorizontal },
];

/**
 * « Autre opération… »: les écritures moins courantes, chacune avec ses seuls
 * champs (même fenêtre, même envoi). Elles remplacent le formulaire générique
 * de l'ancien bloc « Comptes et trésorerie ».
 */
export const AUTRES_OPERATIONS = [
  { cle: 'entree', titre: 'Entrée d’argent diverse' },
  { cle: 'remboursement-chauffeur', titre: 'Remboursement à un chauffeur' },
  { cle: 'avoir-client', titre: 'Avoir à un client' },
  { cle: 'remboursement-client', titre: 'Remboursement à un client' },
  { cle: 'avoir-fournisseur', titre: 'Avoir d’un fournisseur' },
  { cle: 'remboursement-fournisseur', titre: 'Remboursement d’un fournisseur' },
];

const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const asList = (data) => (Array.isArray(data) ? data : data?.items || data?.data?.items || data?.data || []);
const toutesLesPages = (chemin, params = {}) =>
  fetchAllPages(async (page) => asList((await api.get(chemin, { params: { ...params, ...page } })).data)).then((r) => r.items);

/** Ce que chaque geste écrit au journal. */
const NATURE = {
  encaisser: { kind: 'CUSTOMER_RECEIPT', party_type: 'CUSTOMER', succes: 'Encaissement enregistré.' },
  avance: { kind: 'DRIVER_ADVANCE', party_type: 'DRIVER', succes: 'Avance enregistrée.' },
  fournisseur: { kind: 'SUPPLIER_PAYMENT', party_type: 'SUPPLIER', succes: 'Paiement du fournisseur enregistré.' },
  rendu: { kind: 'DRIVER_RETURN', party_type: 'DRIVER', succes: 'Reliquat enregistré.' },
  // Une facture reçue ne déplace pas d'argent: pas de compte.
  'facture-recue': { kind: 'SUPPLIER_BILL', party_type: 'SUPPLIER', sansCompte: true, succes: 'Facture du fournisseur enregistrée.' },
  transfert: { kind: 'TRANSFER', succes: 'Transfert enregistré.' },
  entree: { kind: 'INCOME', succes: 'Entrée enregistrée.' },
  'remboursement-chauffeur': { kind: 'DRIVER_REIMBURSEMENT', party_type: 'DRIVER', succes: 'Remboursement au chauffeur enregistré.' },
  // Un avoir ne déplace pas d'argent; la facture concernée est facultative.
  'avoir-client': { kind: 'CUSTOMER_CREDIT', party_type: 'CUSTOMER', sansCompte: true, piece: true, succes: 'Avoir enregistré.' },
  'avoir-fournisseur': { kind: 'SUPPLIER_CREDIT', party_type: 'SUPPLIER', sansCompte: true, piece: true, succes: 'Avoir du fournisseur enregistré.' },
  // Un remboursement part d'un encaissement (ou d'un règlement) qui a encore du crédit.
  'remboursement-client': { kind: 'CUSTOMER_REFUND', party_type: 'CUSTOMER', origine: true, succes: 'Remboursement enregistré.' },
  'remboursement-fournisseur': { kind: 'SUPPLIER_REFUND', party_type: 'SUPPLIER', origine: true, succes: 'Remboursement du fournisseur enregistré.' },
};

const CHEMIN_TIERS = { CUSTOMER: '/clients', DRIVER: '/drivers', SUPPLIER: '/finance/suppliers' };
const LIBELLE_TIERS = { CUSTOMER: 'Client', DRIVER: 'Chauffeur', SUPPLIER: 'Fournisseur' };
const UN_TIERS = { CUSTOMER: 'un client', DRIVER: 'un chauffeur', SUPPLIER: 'un fournisseur' };
const PREFILL_TIERS = { CUSTOMER: 'client', DRIVER: 'chauffeur', SUPPLIER: 'fournisseur' };

const Champ = ({ libelle: texte, children, className }) => (
  <div className={className}>
    <span className="mb-1 block text-sm font-medium">{texte}</span>
    {children}
  </div>
);

/**
 * La fenêtre d'un mouvement d'argent: encaisser, avance, payer un fournisseur,
 * caisse ↔ banque. Exportée pour les tests.
 */
export function FormulaireMouvement({ geste, prefill = {}, onTermine, onAnnuler }) {
  const nature = NATURE[geste];
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [comptes, setComptes] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [factures, setFactures] = useState([]);
  const [origines, setOrigines] = useState([]);
  const [chargement, setChargement] = useState(true);
  const tiersInitial = nature.party_type ? prefill[PREFILL_TIERS[nature.party_type]] : '';
  const [form, setForm] = useState({
    tiers: tiersInitial || '',
    montant: '',
    compte: prefill.compte || '',
    cible: '',
    date: aujourdhui(),
    note: '',
    // Ouverte depuis une facture (Factures, panneau du client): elle est déjà choisie.
    facture: (geste === 'encaisser' && prefill.facture) || '',
    origine: '',
    statut: 'CONFIRMED',
  });
  const [cle, setCle] = useState(financialKey);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [plusOptions, setPlusOptions] = useState(false);

  const modifier = (champ, valeur) => {
    setForm((ancien) => ({ ...ancien, [champ]: valeur }));
    // Une saisie modifiée est une nouvelle demande: nouvelle clé.
    setCle(financialKey());
    setErreur('');
  };

  // Comptes, et la liste des tiers du geste (toutes les pages, pour la recherche).
  useEffect(() => {
    let actif = true;
    const chemin = CHEMIN_TIERS[nature.party_type];
    Promise.all([
      api.get('/finance/accounts').then((r) => asList(r.data)),
      !chemin ? Promise.resolve([]) : chemin === '/finance/suppliers'
        ? api.get(chemin).then((r) => asList(r.data))
        : toutesLesPages(chemin),
    ]).then(([listeComptes, listeTiers]) => {
      if (!actif) return;
      setComptes(listeComptes);
      setTiers(listeTiers);
      setForm((ancien) => ({ ...ancien, compte: ancien.compte || compteParDefaut(listeComptes) }));
    }).catch((e) => {
      if (actif) setErreur(messageErreur(e, 'Impossible de charger les comptes et la liste.'));
    }).finally(() => {
      if (actif) setChargement(false);
    });
    return () => {
      actif = false;
    };
    // `nature` suit `geste`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geste]);

  // Factures encore ouvertes du client ou du fournisseur choisi, de la plus ancienne à la plus récente.
  useEffect(() => {
    let actif = true;
    setFactures([]);
    if (!form.tiers || !(['encaisser', 'fournisseur'].includes(geste) || nature.piece)) return undefined;
    const lire = nature.party_type === 'CUSTOMER'
      ? toutesLesPages('/invoices', { client_id: form.tiers }).then((liste) => liste
        // La liste des factures donne le reste dû en `balance_due`, la fiche en `amount_remaining`.
        .map((f) => ({ ...f, reste: Number(f.amount_remaining ?? f.balance_due ?? 0) }))
        .filter((f) => f.client_id === form.tiers && !['CANCELLED', 'PAID'].includes(f.status) && f.reste > 0)
        .sort((a, b) => String(a.invoice_date || '').localeCompare(String(b.invoice_date || '')))
        .map((f) => ({ id: f.id, reference: f.invoice_number, reste: f.reste, date: f.invoice_date })))
      : api.get('/finance/statement', { params: { party_type: 'SUPPLIER', party_id: form.tiers } }).then((r) => (r.data?.data?.documents || [])
        .filter((doc) => doc.status !== 'PAID' && Number(doc.remaining) > 0)
        .map((doc) => ({ id: doc.id, reference: doc.reference, reste: Number(doc.remaining) })));
    lire.then((liste) => {
      if (!actif) return;
      setFactures(liste);
      // Facture choisie d'avance: son reste devient le montant proposé.
      setForm((ancien) => {
        const choisie = liste.find((f) => f.id === ancien.facture);
        return choisie && !ancien.montant ? { ...ancien, montant: String(choisie.reste) } : ancien;
      });
    }).catch(() => actif && setFactures([]));
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geste, form.tiers]);

  // Remboursement: les encaissements (ou règlements) du tiers qui ont encore du crédit.
  useEffect(() => {
    let actif = true;
    setOrigines([]);
    if (!form.tiers || !nature.origine) return undefined;
    api.get('/finance/statement', { params: { party_type: nature.party_type, party_id: form.tiers } }).then((r) => {
      if (!actif) return;
      const corps = r.data?.data || {};
      const operations = new Map((corps.operations || []).map((op) => [op.id, op]));
      setOrigines((corps.available_credits || [])
        .filter((credit) => Number(credit.amount) > 0)
        .map((credit) => ({ id: credit.operation_id, disponible: Number(credit.amount), operation: operations.get(credit.operation_id) || {} })));
    }).catch(() => actif && setOrigines([]));
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geste, form.tiers]);

  const optionsTiers = useMemo(() => tiers.map((row) => ({
    value: row.id,
    label: nomLisible(row.name || row.full_name, 'Sans nom'),
    description: [row.city, row.phone].filter(Boolean).join(' · ') || undefined,
    keywords: [row.contact_name, row.tax_id, row.license_number],
  })), [tiers]);
  const optionsComptes = useMemo(() => comptes.map((row) => ({
    value: row.id,
    label: nomLisible(row.name, 'Compte sans nom'),
    description: libelle('compteTresorerie', row.account_type),
  })), [comptes]);
  const optionsOrigines = useMemo(() => origines.map((o) => ({
    value: o.id,
    label: `${libelle('financeKind', o.operation.kind)} du ${String(o.operation.occurred_at || '').slice(0, 10).split('-').reverse().join('/')}`,
    description: `crédit disponible ${formatMontant(o.disponible)}`,
  })), [origines]);
  const optionsFactures = useMemo(() => [
    { value: '', label: nature.piece ? 'Sans facture précise' : 'Sans affecter de facture' },
    ...factures.map((f) => ({
      value: f.id,
      label: referenceLisible(f.reference, 'Facture sans numéro'),
      description: `reste ${formatMontant(f.reste)}`,
    })),
  ], [factures, nature.piece]);

  const facture = factures.find((f) => f.id === form.facture);
  const origine = origines.find((o) => o.id === form.origine);
  const montant = Number(form.montant);
  const manque = [
    nature.party_type && !form.tiers ? ({ CUSTOMER: 'le client', DRIVER: 'le chauffeur', SUPPLIER: 'le fournisseur' })[nature.party_type] : null,
    nature.origine && !form.origine ? 'l’encaissement d’origine' : null,
    // Le serveur refuse un remboursement plus grand que le crédit restant.
    origine && montant > origine.disponible ? `un montant d’au plus ${formatMontant(origine.disponible)}` : null,
    !(montant > 0) ? 'le montant' : null,
    !form.compte && !nature.sansCompte ? 'le compte' : null,
    geste === 'transfert' && !form.cible ? 'le compte destinataire' : null,
    !form.date ? 'la date' : null,
  ].filter(Boolean);

  const choisirFacture = (valeur) => {
    const choisie = factures.find((f) => f.id === valeur);
    setForm((ancien) => ({
      ...ancien,
      facture: valeur,
      // Le reste de la facture devient le montant proposé, tant qu'aucun n'est saisi.
      montant: ancien.montant || (choisie ? String(choisie.reste) : ''),
    }));
    setCle(financialKey());
  };

  const enregistrer = async () => {
    if (manque.length || envoi) return;
    setEnvoi(true);
    setErreur('');
    const corps = {
      idempotency_key: cle,
      kind: nature.kind,
      amount: montant,
      currency: 'XOF',
      ...(nature.sansCompte ? {} : { account_id: form.compte }),
      occurred_at: form.date,
      status: form.statut,
      details: form.note.trim() ? { notes: form.note.trim() } : {},
      allocations: facture && !nature.piece ? [{ document_id: facture.id, amount: Math.min(montant, facture.reste) }] : [],
    };
    // Un avoir porte sa facture comme pièce; un remboursement, son écriture d'origine.
    if (nature.piece && facture) corps.document_id = facture.id;
    if (nature.origine) corps.original_id = form.origine;
    if (nature.party_type) {
      corps.party_type = nature.party_type;
      corps.party_id = form.tiers;
    }
    if (geste === 'transfert') corps.target_account_id = form.cible;
    try {
      await durableBusinessPost('finance_operation', 'create', '/finance/operations', corps);
      if (!nature.sansCompte) retenirCompte(form.compte);
      toast.success(nature.succes);
      onTermine?.();
    } catch (e) {
      setErreur(messageErreur(e, 'L’opération n’a pas pu être enregistrée. Vérifiez les champs puis réessayez.'));
    } finally {
      setEnvoi(false);
    }
  };

  const libelleTiers = LIBELLE_TIERS[nature.party_type];
  return (
    <div className="space-y-3" data-testid={`formulaire-${geste}`}>
      {erreur && <p role="alert" className="text-sm text-destructive">{erreur}</p>}
      {libelleTiers && (
        <Champ libelle={libelleTiers}>
          <SearchableSelect
            aria-label={libelleTiers}
            value={form.tiers}
            onValueChange={(valeur) => {
              modifier('tiers', valeur);
              modifier('facture', '');
              modifier('origine', '');
            }}
            options={optionsTiers}
            disabled={chargement}
            placeholder={chargement ? 'Chargement…' : `Choisir ${UN_TIERS[nature.party_type]}`}
            searchPlaceholder="Rechercher…"
            emptyMessage="Aucun résultat"
          />
        </Champ>
      )}
      {nature.origine && form.tiers && (
        <Champ libelle={nature.party_type === 'CUSTOMER' ? 'Encaissement remboursé' : 'Règlement remboursé'}>
          <SearchableSelect
            aria-label="Écriture d’origine"
            value={form.origine}
            onValueChange={(valeur) => {
              const choisie = origines.find((o) => o.id === valeur);
              setForm((ancien) => ({ ...ancien, origine: valeur, montant: ancien.montant || (choisie ? String(choisie.disponible) : '') }));
              setCle(financialKey());
            }}
            options={optionsOrigines}
            placeholder={origines.length ? 'Choisir l’écriture' : 'Aucun crédit à rembourser'}
            disabled={!origines.length}
            searchPlaceholder="Rechercher…"
            emptyMessage="Aucune écriture"
          />
        </Champ>
      )}
      {factures.length > 0 && (
        <Champ libelle={nature.piece ? 'Facture concernée (facultatif)' : geste === 'encaisser' ? 'Facture réglée (la plus ancienne d’abord)' : 'Facture du fournisseur'}>
          <SearchableSelect
            aria-label="Facture"
            value={form.facture}
            onValueChange={choisirFacture}
            options={optionsFactures}
            searchPlaceholder="Rechercher une facture…"
            emptyMessage="Aucune facture"
          />
        </Champ>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ libelle="Montant (FCFA)">
          <Input aria-label="Montant" type="number" min="1" inputMode="numeric" value={form.montant} onChange={(e) => modifier('montant', e.target.value)} />
        </Champ>
        <Champ libelle="Date">
          <Input aria-label="Date" type="date" value={form.date} onChange={(e) => modifier('date', e.target.value)} />
        </Champ>
        {!nature.sansCompte && (
          <Champ libelle={geste === 'transfert' ? 'Depuis le compte' : ['encaisser', 'rendu', 'entree', 'remboursement-fournisseur'].includes(geste) ? 'Reçu sur le compte' : 'Payé depuis le compte'}>
            <SearchableSelect
              aria-label="Compte"
              value={form.compte}
              onValueChange={(valeur) => modifier('compte', valeur)}
              options={optionsComptes}
              placeholder="Choisir la caisse ou la banque"
              searchPlaceholder="Rechercher un compte…"
              emptyMessage="Aucun compte"
            />
          </Champ>
        )}
        {geste === 'transfert' && (
          <Champ libelle="Vers le compte">
            <SearchableSelect
              aria-label="Compte destinataire"
              value={form.cible}
              onValueChange={(valeur) => modifier('cible', valeur)}
              options={optionsComptes.filter((option) => option.value !== form.compte)}
              placeholder="Choisir le compte"
              searchPlaceholder="Rechercher un compte…"
              emptyMessage="Aucun autre compte"
            />
          </Champ>
        )}
        <Champ libelle={nature.sansCompte ? 'Référence de la pièce' : 'Référence ou note'} className={geste === 'transfert' || nature.sansCompte ? 'sm:col-span-2' : undefined}>
          <Input aria-label="Référence ou note" value={form.note} onChange={(e) => modifier('note', e.target.value)} />
        </Champ>
      </div>
      {facture && !nature.piece && montant > facture.reste && (
        <p className="text-sm text-muted-foreground">
          {formatMontant(facture.reste)} affectés à la facture, {formatMontant(montant - facture.reste)} en crédit au compte du client.
        </p>
      )}
      {geste === 'encaisser' && (plusOptions ? (
        <Champ libelle="État">
          <select
            aria-label="État"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={form.statut}
            onChange={(e) => modifier('statut', e.target.value)}
          >
            <option value="CONFIRMED">{libelle('financeStatut', 'CONFIRMED')}</option>
            <option value="PENDING">En attente de confirmation (chèque…)</option>
          </select>
        </Champ>
      ) : (
        <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => setPlusOptions(true)}>Plus d’options</button>
      ))}
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onAnnuler}>Annuler</Button>
        <Button
          type="button"
          onClick={enregistrer}
          disabled={envoi || manque.length > 0 || !enLigne}
          title={!enLigne ? 'Réseau nécessaire' : manque.length ? `Indiquez ${manque.join(', ')}` : undefined}
        >
          {envoi ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * La fenêtre d'un geste, ouverte depuis un autre endroit que le menu: le
 * bouton « Encaisser » de Factures et du panneau d'un client, « Avance » d'un
 * chauffeur, « Payer » d'un fournisseur. Même formulaire, même envoi.
 */
export function FenetreGeste({ geste, ouvert, prefill = {}, onFermer, onTermine }) {
  const courant = GESTES.find((g) => g.cle === geste);
  return (
    <Dialog open={Boolean(ouvert && courant)} onOpenChange={(o) => !o && onFermer?.()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{courant?.titre}</DialogTitle>
        </DialogHeader>
        {ouvert && courant && (
          <FormulaireMouvement
            key={`${geste}|${prefill.client || ''}|${prefill.facture || ''}|${prefill.chauffeur || ''}|${prefill.fournisseur || ''}`}
            geste={geste}
            prefill={prefill}
            onTermine={onTermine}
            onAnnuler={onFermer}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** « Encaisser un client »: la même fenêtre sur l'écran Argent et sur Factures. */
export const EncaisserClient = ({ client, facture, ...props }) => (
  <FenetreGeste geste="encaisser" prefill={{ client, facture }} {...props} />
);

/** « Autre opération… »: choisir la nature, puis la fenêtre courte de cette nature. */
export function AutreOperation({ prefill = {}, onTermine, onAnnuler }) {
  const [choix, setChoix] = useState(AUTRES_OPERATIONS[0].cle);
  return (
    <div className="space-y-3">
      <Champ libelle="Nature">
        <select
          aria-label="Nature de l’opération"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={choix}
          onChange={(e) => setChoix(e.target.value)}
        >
          {AUTRES_OPERATIONS.map((o) => <option key={o.cle} value={o.cle}>{o.titre}</option>)}
        </select>
      </Champ>
      <FormulaireMouvement key={choix} geste={choix} prefill={prefill} onTermine={onTermine} onAnnuler={onAnnuler} />
    </div>
  );
}

export default function NouvelleOperation({ prefill = {}, onEnregistre }) {
  const [geste, setGeste] = useState(null);
  const courant = GESTES.find((g) => g.cle === geste);
  const fermer = () => setGeste(null);
  const termine = () => {
    setGeste(null);
    onEnregistre?.();
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" data-testid="nouvelle-operation">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle opération
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {GESTES.filter((g) => g.menu).map(({ cle, menu, icone: Icone }) => (
            <DropdownMenuItem key={cle} onSelect={() => setGeste(cle)} className="gap-2">
              <Icone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {menu}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={Boolean(courant)} onOpenChange={(ouvert) => !ouvert && fermer()}>
        <DialogContent className={geste === 'depense' ? 'max-h-[90vh] max-w-2xl overflow-y-auto' : 'max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{courant?.titre}</DialogTitle>
          </DialogHeader>
          {geste === 'depense' && <ExpenseForm enFenetre onSaved={() => { toast.success('Dépense enregistrée.'); termine(); }} />}
          {geste === 'autre' && <AutreOperation prefill={prefill} onTermine={termine} onAnnuler={fermer} />}
          {courant && !['depense', 'autre'].includes(geste) && (
            <FormulaireMouvement key={geste} geste={geste} prefill={prefill} onTermine={termine} onAnnuler={fermer} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
